import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Plus, Pencil, Trash2, ArrowUpCircle, ArrowDownCircle, Lock, Upload, FileText } from 'lucide-react';
import { toast } from 'sonner';
import SearchBar from '../components/SearchBar';
import ExportMenu from '../components/ExportMenu';

const MONTHS = ['01','02','03','04','05','06','07','08','09','10','11','12'];
const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const CATEGORIES = ['Client Payment', 'Office Expense', 'Salary', 'Rent', 'Utility', 'Professional Fees', 'Tax Payment', 'Refund', 'Investment', 'Other'];

export default function CashLedgerPage() {
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [monthFilter, setMonthFilter] = useState(String(new Date().getMonth() + 1).padStart(2, '0'));
  const [yearFilter, setYearFilter] = useState(String(new Date().getFullYear()));
  const [searchText, setSearchText] = useState('');
  const [form, setForm] = useState({ date: '', type: 'Credit', amount: 0, description: '', reference: '', category: '' });
  const [uploadFile, setUploadFile] = useState(null);
  const fileInputRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (monthFilter) params.set('month', monthFilter);
      if (yearFilter) params.set('year', yearFilter);
      const [entRes, sumRes] = await Promise.all([
        api.get(`/cash-ledger?${params}`),
        api.get(`/cash-ledger/summary?${params}`)
      ]);
      setEntries(entRes.data);
      setSummary(sumRes.data);
    } catch (err) {
      if (err.response?.status === 403) toast.error('Admin access required');
      else toast.error('Failed to load data');
    }
    finally { setLoading(false); }
  }, [monthFilter, yearFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openNew = () => {
    setEditId(null);
    setForm({ date: new Date().toISOString().split('T')[0], type: 'Credit', amount: 0, description: '', reference: '', category: '' });
    setUploadFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setDialogOpen(true);
  };

  const openEdit = (entry) => {
    setEditId(entry._id);
    setForm({ date: entry.date, type: entry.type, amount: entry.amount, description: entry.description || '', reference: entry.reference || '', category: entry.category || '' });
    setUploadFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setDialogOpen(true);
  };

  const uploadDoc = async (entryId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    try {
      await api.post(`/cash-ledger/${entryId}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Document uploaded');
    } catch {
      toast.error('Document upload failed');
    }
  };

  const handleSubmit = async () => {
    if (!form.date || !form.amount) { toast.error('Date and amount required'); return; }
    try {
      let entryId = editId;
      if (editId) {
        await api.put(`/cash-ledger/${editId}`, form);
        toast.success('Updated');
      } else {
        const res = await api.post('/cash-ledger', form);
        entryId = res.data.id;
        toast.success('Entry created');
      }
      if (uploadFile && entryId) {
        await uploadDoc(entryId, uploadFile);
      }
      setDialogOpen(false);
      setUploadFile(null);
      fetchData();
    } catch { toast.error('Failed to save'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    try { await api.delete(`/cash-ledger/${id}`); toast.success('Deleted'); fetchData(); } catch { toast.error('Failed'); }
  };

  const handleDocDownload = async (entryId, docIndex, filename) => {
    try {
      const res = await api.get(`/cash-ledger/${entryId}/documents/${docIndex}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = filename || 'document'; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const handleUploadToExisting = async (entryId, file) => {
    await uploadDoc(entryId, file);
    fetchData();
  };

  // Filter + Running balance calculation
  const filtered = searchText
    ? entries.filter(e => {
        const q = searchText.toLowerCase();
        return (e.description || '').toLowerCase().includes(q) ||
               (e.reference || '').toLowerCase().includes(q) ||
               (e.category || '').toLowerCase().includes(q);
      })
    : entries;
  const sorted = [...filtered].sort((a, b) => a.date.localeCompare(b.date));
  let runBal = 0;
  const withBalance = sorted.map(e => {
    if (e.type === 'Credit') runBal += e.amount;
    else runBal -= e.amount;
    return { ...e, balance: runBal };
  });

  return (
    <div data-testid="cash-ledger-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <Lock size={24} className="text-[#D4AF37]" />
          <div>
            <h1 className="font-heading text-4xl font-semibold text-[#111B11] tracking-tight">Cash Ledger</h1>
            <p className="text-sm text-[#4F734F] mt-1">Admin-only cash flow tracking</p>
          </div>
        </div>
        <div className="flex gap-2">
          <ExportMenu
            endpoint="/exports/cash-ledger"
            params={{ ...(monthFilter ? { month: monthFilter } : {}), ...(yearFilter ? { year: yearFilter } : {}) }}
            filename="cash_ledger"
            testIdPrefix="ledger"
          />
          <Button onClick={openNew} className="btn-accent gap-2" data-testid="add-ledger-btn"><Plus size={16} /> New Entry</Button>
        </div>
      </div>

      {/* Summary */}
      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="stat-card"><p className="text-xs uppercase font-semibold text-[#4F734F] mb-1">Total Credit</p><p className="font-heading text-2xl font-light text-green-600">{summary.total_credit?.toLocaleString('en-IN')}</p></div>
          <div className="stat-card"><p className="text-xs uppercase font-semibold text-[#4F734F] mb-1">Total Debit</p><p className="font-heading text-2xl font-light text-red-600">{summary.total_debit?.toLocaleString('en-IN')}</p></div>
          <div className="stat-card"><p className="text-xs uppercase font-semibold text-[#4F734F] mb-1">Net Balance</p><p className={`font-heading text-2xl font-light ${summary.balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>{summary.balance?.toLocaleString('en-IN')}</p></div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <SearchBar
          value={searchText}
          onChange={setSearchText}
          placeholder="Search description, reference, category..."
          testId="ledger-search"
          minWidth="min-w-[280px]"
          className="!flex-none w-full sm:w-80"
        />
        <Select value={monthFilter} onValueChange={setMonthFilter}>
          <SelectTrigger className="w-40 h-11 input-field"><SelectValue /></SelectTrigger>
          <SelectContent>{MONTHS.map((m, i) => <SelectItem key={m} value={m}>{MONTH_NAMES[i]}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={yearFilter} onValueChange={setYearFilter}>
          <SelectTrigger className="w-28 h-11 input-field"><SelectValue /></SelectTrigger>
          <SelectContent>{['2024','2025','2026'].map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="bg-white border border-[#E6EDE6] rounded-sm overflow-hidden">
        <table className="w-full text-sm text-left">
          <thead><tr className="bg-[#1B2A1B] text-white">
            <th className="py-2.5 px-3 font-medium text-xs tracking-wider">Date</th>
            <th className="py-2.5 px-3 font-medium text-xs tracking-wider">Type</th>
            <th className="py-2.5 px-3 font-medium text-xs tracking-wider">Category</th>
            <th className="py-2.5 px-3 font-medium text-xs tracking-wider">Description</th>
            <th className="py-2.5 px-3 font-medium text-xs tracking-wider">Reference</th>
            <th className="py-2.5 px-3 font-medium text-xs tracking-wider text-right">Amount</th>
            <th className="py-2.5 px-3 font-medium text-xs tracking-wider text-right">Balance</th>
            <th className="py-2.5 px-3 font-medium text-xs tracking-wider">Docs</th>
            <th className="py-2.5 px-3 font-medium text-xs tracking-wider w-16">Actions</th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={9} className="text-center py-8 text-olive-400">Loading...</td></tr> :
            withBalance.length === 0 ? <tr><td colSpan={9} className="text-center py-8 text-olive-400">No entries</td></tr> :
            withBalance.map(e => (
              <tr key={e._id} className="border-b border-[#E6EDE6] hover:bg-[#F8FAF7]" data-testid={`ledger-row-${e._id}`}>
                <td className="py-2 px-3">{e.date}</td>
                <td className="py-2 px-3">
                  <div className="flex items-center gap-1.5">
                    {e.type === 'Credit' ? <ArrowUpCircle size={13} className="text-green-500" /> : <ArrowDownCircle size={13} className="text-red-500" />}
                    <span className={e.type === 'Credit' ? 'text-green-700' : 'text-red-700'}>{e.type}</span>
                  </div>
                </td>
                <td className="py-2 px-3 text-xs">{e.category || '-'}</td>
                <td className="py-2 px-3">{e.description || '-'}</td>
                <td className="py-2 px-3 text-xs font-mono">{e.reference || '-'}</td>
                <td className={`py-2 px-3 text-right font-mono font-medium ${e.type === 'Credit' ? 'text-green-700' : 'text-red-700'}`}>
                  {e.type === 'Credit' ? '+' : '-'}{e.amount?.toLocaleString('en-IN')}
                </td>
                <td className={`py-2 px-3 text-right font-mono font-medium ${e.balance >= 0 ? 'text-green-700' : 'text-red-700'}`}>{e.balance?.toLocaleString('en-IN')}</td>
                <td className="py-2 px-3">
                  <div className="flex items-center gap-1 flex-wrap">
                    {(e.documents || []).map((doc, i) => (
                      <button
                        key={i}
                        onClick={() => handleDocDownload(e._id, i, doc.filename)}
                        className="p-1 hover:bg-olive-50 rounded-sm"
                        title={doc.filename}
                        data-testid={`ledger-doc-${e._id}-${i}`}
                      >
                        <FileText size={13} className="text-[#D4AF37]" />
                      </button>
                    ))}
                    <label className="p-1 hover:bg-olive-50 rounded-sm cursor-pointer" title="Upload document">
                      <Upload size={13} className="text-olive-500" />
                      <input
                        type="file"
                        className="hidden"
                        data-testid={`ledger-upload-${e._id}`}
                        onChange={(ev) => {
                          const f = ev.target.files?.[0];
                          if (f) handleUploadToExisting(e._id, f);
                          ev.target.value = '';
                        }}
                      />
                    </label>
                  </div>
                </td>
                <td className="py-2 px-3">
                  <div className="flex gap-0.5">
                    <button onClick={() => openEdit(e)} className="p-1 hover:bg-olive-100 rounded-sm" data-testid={`edit-ledger-${e._id}`}><Pencil size={13} className="text-olive-500" /></button>
                    <button onClick={() => handleDelete(e._id)} className="p-1 hover:bg-red-50 rounded-sm" data-testid={`delete-ledger-${e._id}`}><Trash2 size={13} className="text-red-400" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-xl">{editId ? 'Edit' : 'New'} Ledger Entry</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label className="text-xs uppercase font-semibold text-[#4F734F]">Date</Label><Input type="date" value={form.date} onChange={(e) => setForm(prev => ({ ...prev, date: e.target.value }))} className="input-field" /></div>
              <div className="space-y-1.5"><Label className="text-xs uppercase font-semibold text-[#4F734F]">Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm(prev => ({ ...prev, type: v }))}>
                  <SelectTrigger className="input-field"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="Credit">Credit</SelectItem><SelectItem value="Debit">Debit</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label className="text-xs uppercase font-semibold text-[#4F734F]">Amount (INR)</Label><Input type="number" value={form.amount} onChange={(e) => setForm(prev => ({ ...prev, amount: Number(e.target.value) }))} className="input-field" /></div>
              <div className="space-y-1.5"><Label className="text-xs uppercase font-semibold text-[#4F734F]">Category</Label>
                <Select value={form.category || 'none'} onValueChange={(v) => setForm(prev => ({ ...prev, category: v === 'none' ? '' : v }))}>
                  <SelectTrigger className="input-field"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">None</SelectItem>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5"><Label className="text-xs uppercase font-semibold text-[#4F734F]">Description</Label><Input value={form.description} onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))} className="input-field" placeholder="What is this for?" /></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase font-semibold text-[#4F734F]">Reference / Cheque No.</Label><Input value={form.reference} onChange={(e) => setForm(prev => ({ ...prev, reference: e.target.value }))} className="input-field" placeholder="Ref number" /></div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase font-semibold text-[#4F734F]">Attach Document (optional)</Label>
              <Input
                ref={fileInputRef}
                type="file"
                accept="image/*,application/pdf"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                className="input-field"
                data-testid="ledger-form-upload"
              />
              {uploadFile && <p className="text-xs text-[#4F734F]">Selected: {uploadFile.name}</p>}
            </div>
            <div className="flex gap-3 pt-2">
              <Button onClick={handleSubmit} className="btn-primary" data-testid="save-ledger-btn">{editId ? 'Update' : 'Create'}</Button>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
